import { isValidSortOrder } from "@/modules/ui/components/dataTables/datatableUtils"
import type { Page } from "@/modules/utils/paginated"
import { DEFAULT_PAGE_SIZE } from "@/modules/utils/paginated"
import { useServicesContext } from "@modules/core/services/services.context"
import useDebounce from "@modules/hooks/useDebounce"
import { MemberDetailedEntity, MemberItemEntity } from "@modules/members/entities/memberEntity"
import {
  MemberColumnToSort,
  type MemberDetailedDto,
  MemberFilter,
  type MemberItemDto,
} from "@modules/members/gateway/memberDto"
import { ColumnSortingOrder } from "@modules/types/datatableTypes"
import { usePersistedDatatable } from "@modules/utils/hooks/useDataTable"
import { useSetMultiParams } from "@modules/utils/navigation/useQueryParam"
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { useSearchParams } from "react-router"

export const useMembers = ({
  filter,
  nameFilter,
  page,
  pageSize,
  sortBy,
  sortOrder,
}: {
  filter: MemberFilter
  nameFilter?: string
  page?: number
  pageSize?: number
  sortBy?: string
  sortOrder?: string
}) => {
  const { memberApi } = useServicesContext()

  const { data, isLoading } = useSuspenseQuery({
    queryKey: ["boxMembers", { filter, page, pageSize, nameFilter, sortBy, sortOrder }],
    queryFn: async () => {
      if (sortBy && isValidMemberSortBy(sortBy) && sortOrder && isValidSortOrder(sortOrder)) {
        return await memberApi.getMembers(filter, page, pageSize, nameFilter, sortBy, sortOrder)
      }

      return await memberApi.getMembers(filter, page, pageSize, nameFilter)
    },
  })

  return {
    data: data.content.map((boxMember) => MemberItemEntity.fromItemDto(boxMember)),
    isLoading,
    totalElements: data.totalSize,
    hasPreviousPage: data.page > 0,
    hasNextPage: data.page < data.totalPages - 1,
  }
}

export const usePersistedMembers = (memberTab: MemberFilter) => {
  const [searchParams] = useSearchParams()
  const setListParams = useSetMultiParams()

  const sortBy = searchParams.get("sortBy") ?? undefined
  const sortOrder = searchParams.get("order") ?? undefined
  const nameFilter = searchParams.get("nameFilter") || undefined

  const debouncedSearchTerm = useDebounce(nameFilter, 300)

  const { page, setPage, size: pageSize, setSize: setPageSize } = usePersistedDatatable(DEFAULT_PAGE_SIZE)

  const { data, isLoading, hasPreviousPage, hasNextPage, totalElements } = useMembers({
    filter: memberTab,
    nameFilter: debouncedSearchTerm,
    page,
    pageSize,
    sortBy,
    sortOrder,
  })

  const handlePreviousPage = () => {
    if (hasPreviousPage) {
      setPage((prev) => prev - 1)
    }
  }

  const handleNextPage = () => {
    if (hasNextPage) {
      setPage((prev) => prev + 1)
    }
  }

  const setSorting = (field: MemberColumnToSort, order: ColumnSortingOrder) => {
    if (field === sortBy && order === sortOrder) {
      setListParams([
        { key: "sortBy", value: undefined },
        { key: "order", value: undefined },
      ])
    } else {
      setListParams([
        { key: "sortBy", value: field },
        { key: "order", value: order },
      ])
    }
  }

  return {
    data,
    totalElements,
    isLoading,
    pageSize,
    setPageSize,
    handlePreviousPage,
    handleNextPage,
    hasPreviousPage,
    hasNextPage,
    setSorting,
    sortBy,
    sortOrder,
    isSearching: !!nameFilter,
  }
}

export const useMember = (id: string) => {
  const { memberApi } = useServicesContext()

  const { data, error } = useSuspenseQuery({
    queryKey: ["boxMember", id],
    queryFn: async () => {
      return await memberApi.getMember(id)
    },
  })

  return {
    data: MemberDetailedEntity.fromDto(data),
    error,
  }
}

export const useMemberActions = (id: string) => {
  const { memberApi } = useServicesContext()
  const queryClient = useQueryClient()

  // Update member
  const { mutateAsync: updateMember, isPending: isUpdateMember } = useMutation({
    mutationFn: async (member: MemberDetailedEntity) => {
      return await memberApi.updateMember(id, member)
    },
    onSuccess: (data: MemberDetailedDto) => {
      void queryClient.invalidateQueries({
        queryKey: ["boxMember", id],
      })

      // Update the single member in the list
      // Currently only the note can be edited on the member
      void queryClient.setQueriesData(
        { predicate: (query) => query.queryKey[0] === "boxMembers" },
        (oldData: Page<MemberItemDto> | undefined) => ({
          ...oldData,
          content: oldData?.content.map((member) => {
            if (member.id !== id) return member
            return {
              ...member,
              note: data.note,
            }
          }),
        }),
      )
    },
  })

  // Archive member
  const { mutateAsync: archiveMember, isPending: isArchiveMember } = useMutation({
    mutationFn: async () => {
      await memberApi.archiveMember(id)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["boxMembers"],
      })
      void queryClient.invalidateQueries({
        queryKey: ["boxMember", id],
      })
      void queryClient.invalidateQueries({
        queryKey: ["boxArchivedMembers"],
      })
    },
  })

  const isPending = isUpdateMember || isArchiveMember

  return {
    updateMember,
    archiveMember,
    isPending,
  }
}

export function isValidMemberSortBy(sortBy: string): sortBy is MemberColumnToSort {
  return Object.values(MemberColumnToSort).includes(sortBy as MemberColumnToSort)
}
